import React, { useCallback, useMemo, useState } from 'react';

import {
  View,
  TextInput,
  Pressable,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Dimensions,
  View as RNView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { MaterialIcons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { ChargerAttributes } from '../models/charger-model';
import { RootState } from '../store';
import { getAllChargers, getMyDrivingTime } from '../store/actions/charging-actions';
import { chargerActions } from '../store/slices/charger-slice';
import { AppDispatch } from '../store';
import * as Location from 'expo-location';
import { notificationService } from '../services/notification-service';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import PaginationComponent from '../components/PaginationComponent';
import SearchChargerCard from '../components/SearchChargerCard';
import PullToRefreshWrapper from '../components/common/PullToRefreshWrapper';

interface SearchChargerProps {
  navigation: any;
  myLocation: Location.LocationObjectCoords;
  chargersWithDistance: ChargerAttributes[];
}

const SearchCharger: React.FC<SearchChargerProps> = ({
  navigation,
  myLocation,
  chargersWithDistance,
}) => {
  const { height } = Dimensions.get('window');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [query, setQuery] = useState('');

  const [loading, setLoading] = useState(false);
  const [selectedChargerId, setSelectedChargerId] = useState<string | null>(
    null
  );

  const [uuidInfo, setUuid] = useState<String>('');

  const driverPlan: UserPlanAssociation | null = useSelector(
    (state: RootState) => state.user.driverPlan
  );

  const dispatch: AppDispatch = useDispatch();

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearchQuery = useCallback((searchTerm: string) => {
    setQuery(searchTerm);
  }, []);

  // Memoize filtered chargers based on the query
  const searchFilteredChargers = useMemo(() => {
    return chargersWithDistance.filter((charger) => {
      return (
        charger.unit_serial.toLowerCase().includes(query.toLowerCase()) ||
        charger.name.toLowerCase().includes(query.toLowerCase()) ||
        charger.city.toLowerCase().includes(query.toLowerCase())
      );
    });
  }, [chargersWithDistance, query]);

  // Calculate the paginated data
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchFilteredChargers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleSelectCharger = async (charger: any) => {
    try {
      setLoading(true);
      setSelectedChargerId(charger.uuid);

      const myTime = await getMyDrivingTime(myLocation, charger.uuid);

      dispatch(
        chargerActions.updateSelectedCharger({ selectedCharger: charger })
      );
      navigation.navigate('ChargerStation', {
        myDistanceFromCharger: charger.totalDistance,
        myTimeFromCharger: myTime,
      });
    } catch (error) {
      notificationService.sendNotification(
        'error',
        'Failed to select the charger. Please try again'
      );
      console.error('Error selecting charger:', error);
    } finally {
      setLoading(false);
      setSelectedChargerId(null);
    }
  };

  const totalPages = Math.ceil(searchFilteredChargers.length / itemsPerPage);

  return (
    <View
      style={[
        styles.safeArea,
        { flex: 1, position: 'relative' },
      ]}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <View style={[styles.containerBgColor, { flex: 1 }]}>
          {/* Fixed Search Bar */}
          <View style={{ padding: 16 }}>
            <View style={styles.searchContainer}>
              <View style={styles.searchIcon}>
                <Ionicons name="search" size={22} color="white" />
              </View>
              <TextInput
                placeholder="Search charger by name, city, or serial #"
                style={styles.input}
                placeholderTextColor="#a0aec0"
                value={query}
                onChangeText={handleSearchQuery}
              />
              <Pressable onPress={() => handleSearchQuery('')}>
                <MaterialIcons
                  name="highlight-remove"
                  size={38}
                  color="black"
                  style={styles.clearIcon}
                />
              </Pressable>
            </View>
          </View>

          {/* Card Area and Sticky Pagination in a flex column */}
          <View style={{ flex: 1, flexDirection: 'column' }}>
            {/* Card Area */}
            <PullToRefreshWrapper
              style={{ flex: 1 }}
              contentContainerStyle={{
                paddingBottom: Platform.OS === 'web' ? 16 : 56,
              }}
              pullText="Pull down to refresh search results"
              refreshingText="Refreshing charger data..."
              onRefresh={async () => {
                console.log("getAllChargers");
                await dispatch(getAllChargers());
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }}
              keyboardShouldPersistTaps="handled"
            >
              {currentItems?.map((data, index) => {
                const isSelected = selectedChargerId === data.uuid;

                return (
                  <SearchChargerCard
                    key={index}
                    data={data}
                    isSelected={isSelected}
                    loading={loading}
                    driverPlan={driverPlan}
                    onSelectCharger={handleSelectCharger}
                  />
                );
              })}
            </PullToRefreshWrapper>
            {/* Pagination Bar - sticky on web, fixed on mobile */}
            {totalPages > 1 &&
              (Platform.OS === 'web' ? (
                <div
                  style={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10,
                    background: '#1A1D21',
                    borderTop: '1px solid #222',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                    margin: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 44,
                    height: 44,
                  }}
                >
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                  />
                </div>
              ) : null)}
          </View>
          {/* Fixed-bottom Pagination Bar for mobile */}
          {totalPages > 1 && Platform.OS !== 'web' && (
            <RNView
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                minHeight: 56,
                height: 56,
                backgroundColor: '#1A1D21',
                borderTopWidth: 1,
                borderTopColor: '#222',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 100,
              }}
            >
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            </RNView>
          )}
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  containerBgColor: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 25,
    paddingHorizontal: 20,
  },
  searchIcon: {
    backgroundColor: '#39BEB7',
    borderRadius: 50,
    paddingVertical: 12,
    paddingHorizontal: 13,
    marginRight: 8,
  },
  input: {
    flex: 1,
    paddingVertical: 10,
  },
  clearIcon: {
    marginLeft: 5,
    marginTop: 2,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
  },
});

export default SearchCharger;
